import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Key, X, Save, ShieldCheck, Zap, Bot, Globe, Search, Check, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import type { AIConfig, AIProvider } from "../types";

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    config: AIConfig;
    onSave: (config: AIConfig) => void;
}

const MODELS = {
    google: [
        { id: "gemini-3.0-pro", name: "Gemini 3.0 Pro" },
        { id: "gemini-3.0-flash", name: "Gemini 3.0 Flash" },
        { id: "gemini-2.5-pro", name: "Gemini 2.5 Pro" },
        { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash" },
        { id: "gemini-2.5-flash-lite", name: "Gemini 2.5 Flash Lite" },
        { id: "gemini-2.0-flash-001", name: "Gemini 2.0 Flash" },
        { id: "gemini-1.5-pro-002", name: "Gemini 1.5 Pro" },
        { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash" },
    ],
    openai: [
        { id: "gpt-5.2", name: "GPT-5.2" },
        { id: "gpt-5-mini", name: "GPT-5 Mini" },
        { id: "gpt-5", name: "GPT-5" },
        { id: "gpt-4o", name: "GPT-4o" },
        { id: "gpt-4o-mini", name: "GPT-4o Mini" },
        { id: "o3-mini", name: "o3-mini" },
        { id: "o1", name: "o1" },
    ],
    gateway: [
        // OpenAI
        { id: "openai/gpt-5.2", name: "OpenAI GPT-5.2" },
        { id: "openai/gpt-5-mini", name: "OpenAI GPT-5 Mini" },
        { id: "openai/gpt-5", name: "OpenAI GPT-5" },
        { id: "openai/gpt-4o", name: "OpenAI GPT-4o" },
        { id: "openai/gpt-4o-mini", name: "OpenAI GPT-4o Mini" },
        { id: "openai/o3-mini", name: "OpenAI o3-mini" },
        { id: "openai/o1", name: "OpenAI o1" },

        // Google
        { id: "google/gemini-3-flash", name: "Google Gemini 3.0 Flash" },
        { id: "google/gemini-2.5-pro", name: "Google Gemini 2.5 Pro" },
        { id: "google/gemini-2.5-flash", name: "Google Gemini 2.5 Flash" },
        { id: "google/gemini-2.5-flash-lite", name: "Google Gemini 2.5 Flash Lite" },
        { id: "google/gemini-2.0-flash-lite", name: "Google Gemini 2.0 Flash" },

        // Anthropic
        { id: "anthropic/claude-opus-4.1", name: "Anthropic Claude Opus 4.1" },
        { id: "anthropic/claude-haiku-4.5", name: "Anthropic Claude Haiku 4.5" },
        { id: "anthropic/claude-sonnet-4.5", name: "Anthropic Claude Sonnet 4.5" },

        // Others
        { id: "moonshot/kimi-k2", name: "Moonshot Kimi K2" },
        { id: "moonshot/kimi-k2.5", name: "Moonshot Kimi K2.5" },
        { id: "zai/glm-4.5-air", name: "Zai GLM-4.5 Air" },
        { id: "zai/glm-4.5", name: "Zai GLM-4.5" },
        { id: "xai/grok-3-mini-fast", name: "xAI Grok 3 Mini Fast" },
        { id: "xai/grok-4.1-fast-reasoning", name: "xAI Grok 4.1 Fast Reasoning" },
        { id: "xiaomi/mimo-v2-flash", name: "Xiaomi Mimo V2 Flash" },
        { id: "minimax/minimax-m2.1-lightning", name: "Minimax M2.1 Lightning" },
        { id: "alibaba/qwen-3-14b", name: "Alibaba Qwen 3-14B" },
    ]
};

function Combobox({
    value,
    onChange,
    options,
    placeholder = "Select model..."
}: {
    value: string;
    onChange: (val: string) => void;
    options: { id: string; name: string }[];
    placeholder?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [rect, setRect] = useState<DOMRect | null>(null);
    const [position, setPosition] = useState<"top" | "bottom">("bottom");

    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Filter options
    const filtered = useMemo(() => {
        if (!options) return [];
        if (!search) return options;
        return options.filter(opt =>
            opt.name.toLowerCase().includes(search.toLowerCase()) ||
            opt.id.toLowerCase().includes(search.toLowerCase())
        );
    }, [options, search]);

    // Handle interactions
    useEffect(() => {
        const handleInteraction = (e: MouseEvent | Event) => {
            if (isOpen) {
                const target = e.target as Node;

                // If scrolling inside the menu, ignore (don't close)
                if (e.type === 'scroll' && menuRef.current?.contains(target)) {
                    return;
                }

                // Check click containment
                const isClickInside =
                    (buttonRef.current && buttonRef.current.contains(target)) ||
                    (menuRef.current && menuRef.current.contains(target));

                if (e.type === 'mousedown' && !isClickInside) {
                    setIsOpen(false);
                } else if (e.type === 'scroll' || e.type === 'resize') {
                    // Re-evaluate position on scroll/resize of window/parents
                    if (buttonRef.current) {
                        const newRect = buttonRef.current.getBoundingClientRect();
                        setRect(newRect);
                        // Optional: close if scroll is signficant or complicates positioning?
                        // For now, let's keep it robust by closing on external scroll to avoid detachment
                        setIsOpen(false);
                    }
                }
            }
        };

        if (isOpen) {
            window.addEventListener("mousedown", handleInteraction);
            window.addEventListener("scroll", handleInteraction, true); // Capture phase to detect all scrolls
            window.addEventListener("resize", handleInteraction);
        }

        return () => {
            window.removeEventListener("mousedown", handleInteraction);
            window.removeEventListener("scroll", handleInteraction, true);
            window.removeEventListener("resize", handleInteraction);
        };
    }, [isOpen]);

    const handleOpen = () => {
        if (!isOpen && buttonRef.current) {
            const r = buttonRef.current.getBoundingClientRect();
            setRect(r);

            // Determine position: if space below < 300px, show above
            const spaceBelow = window.innerHeight - r.bottom;
            setPosition(spaceBelow < 300 ? "top" : "bottom");
        }
        setIsOpen(!isOpen);
    };

    const selectedName = options?.find(o => o.id === value)?.name || value || placeholder;

    return (
        <>
            <button
                ref={buttonRef}
                onClick={handleOpen}
                className="w-full flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
                type="button"
            >
                <span className={!value ? "text-white/40" : ""}>{selectedName}</span>
                <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && rect && createPortal(
                <AnimatePresence>
                    <motion.div
                        ref={menuRef}
                        initial={{ opacity: 0, y: position === "bottom" ? -8 : 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: position === "bottom" ? -8 : 8, scale: 0.95 }}
                        transition={{ duration: 0.1 }}
                        style={{
                            position: "fixed",
                            top: position === "bottom" ? rect.bottom + 8 : "auto",
                            bottom: position === "top" ? window.innerHeight - rect.top + 8 : "auto",
                            left: rect.left,
                            width: rect.width,
                            zIndex: 9999,
                            // Restrict max height based on available space to prevent overflow
                            maxHeight: position === "bottom"
                                ? Math.min(300, window.innerHeight - rect.bottom - 16)
                                : Math.min(300, rect.top - 16)
                        }}
                        className="bg-[#0f1115] border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col"
                    >
                        <div className="p-2 border-b border-white/5 bg-[#0f1115] shrink-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                                <input
                                    autoFocus
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-xs text-white focus:outline-none focus:border-primary/50 placeholder:text-white/20"
                                />
                            </div>
                        </div>

                        <div
                            className="overflow-y-auto p-1 custom-scrollbar min-h-0"
                            onScroll={(e) => e.stopPropagation()} // Stop scroll propagation to prevent closing
                        >
                            {filtered.length === 0 ? (
                                <div className="p-3 text-center text-xs text-white/30">
                                    No models found
                                </div>
                            ) : (
                                filtered.map(opt => (
                                    <button
                                        key={opt.id}
                                        onClick={() => {
                                            onChange(opt.id);
                                            setIsOpen(false);
                                            setSearch("");
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs text-left transition-colors shrink-0 ${value === opt.id
                                            ? "bg-primary/20 text-primary"
                                            : "text-white/70 hover:bg-white/10 hover:text-white"
                                            }`}
                                        type="button"
                                    >
                                        <span>{opt.name}</span>
                                        {value === opt.id && <Check className="w-3.5 h-3.5" />}
                                    </button>
                                ))
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>,
                document.body
            )}
        </>
    );
}

export function SettingsModal({ isOpen, onClose, config, onSave }: SettingsModalProps) {
    const [tempConfig, setTempConfig] = useState<AIConfig>(config);

    useEffect(() => {
        setTempConfig(config);
    }, [config, isOpen]);

    const handleSave = () => {
        onSave(tempConfig);
        onClose();
    };

    const providers: { id: AIProvider; label: string; icon: any }[] = [
        { id: "gateway", label: "AI Gateway", icon: Zap },
        { id: "google", label: "Google Gemini", icon: Globe },
        { id: "openai", label: "OpenAI", icon: Bot },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="relative w-full max-w-lg glass border border-white/10 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
                    >
                        <div className="p-6 space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/10 rounded-xl">
                                        <Key className="w-5 h-5 text-primary" />
                                    </div>
                                    <h2 className="text-xl font-bold">AI Settings</h2>
                                </div>
                                <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            <div className="space-y-6">
                                {/* Provider Selection */}
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-white/60 px-1">AI Provider</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {providers.map((p) => (
                                            <button
                                                key={p.id}
                                                onClick={() => setTempConfig({ ...tempConfig, provider: p.id })}
                                                className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${tempConfig.provider === p.id
                                                    ? "bg-primary/20 border-primary text-primary"
                                                    : "bg-white/5 border-white/10 text-white/40 hover:bg-white/10"
                                                    }`}
                                            >
                                                <p.icon size={20} />
                                                <span className="text-xs font-medium">{p.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Active Provider Settings */}
                                <div className="space-y-4 p-4 bg-white/5 rounded-2xl border border-white/5">
                                    <div className="flex items-center gap-2 mb-2 text-primary">
                                        <Key size={16} />
                                        <span className="text-sm font-bold uppercase tracking-wider">
                                            {providers.find(p => p.id === tempConfig.provider)?.label} Config
                                        </span>
                                    </div>

                                    {tempConfig.provider === "gateway" && (
                                        <div className="space-y-2">
                                            <label className="text-xs text-white/40">Vercel AI Gateway Key</label>
                                            <Input
                                                type="password"
                                                value={tempConfig.keys.gateway || ""}
                                                onChange={(e) => setTempConfig({
                                                    ...tempConfig,
                                                    keys: { ...tempConfig.keys, gateway: e.target.value }
                                                })}
                                                placeholder="sk-..."
                                                className="glass-input"
                                            />
                                        </div>
                                    )}

                                    {tempConfig.provider === "google" && (
                                        <div className="space-y-2">
                                            <label className="text-xs text-white/40">Google Gemini API Key</label>
                                            <Input
                                                type="password"
                                                value={tempConfig.keys.google || ""}
                                                onChange={(e) => setTempConfig({
                                                    ...tempConfig,
                                                    keys: { ...tempConfig.keys, google: e.target.value }
                                                })}
                                                placeholder="AIzaSy..."
                                                className="glass-input"
                                            />
                                        </div>
                                    )}

                                    {tempConfig.provider === "openai" && (
                                        <div className="space-y-2">
                                            <label className="text-xs text-white/40">OpenAI API Key</label>
                                            <Input
                                                type="password"
                                                value={tempConfig.keys.openai || ""}
                                                onChange={(e) => setTempConfig({
                                                    ...tempConfig,
                                                    keys: { ...tempConfig.keys, openai: e.target.value }
                                                })}
                                                placeholder="sk-..."
                                                className="glass-input"
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-2 pt-2 border-t border-white/5">
                                        <label className="text-xs text-white/40">Model Selection</label>
                                        <Combobox
                                            value={tempConfig.model || ""}
                                            onChange={(val) => setTempConfig({ ...tempConfig, model: val })}
                                            options={MODELS[tempConfig.provider]}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-2xl border border-blue-500/10 text-xs text-blue-200/80 leading-relaxed">
                                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                                    <p>
                                        Keys are stored locally in your browser. We never save them to our servers.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <Button variant="ghost" onClick={onClose} className="flex-1 rounded-xl">
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} className="flex-1 rounded-xl shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90">
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Config
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
